import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Alert,
  AppBar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  AlignmentType,
  Document,
  HeadingLevel,
  ImageRun,
  Packer,
  Paragraph,
  TextRun,
} from 'docx';
import { toPng } from 'html-to-image';
import { Category } from '../../types/Category';
import { contentTextLooksRtl } from '../../utils/contentRichPreviewHtml';
import { SubTreeGraph, type GraphicalOrientation } from '../layout/SubTreeView';
import {
  CategoryReportPreview,
  fetchCategoryWithChildren,
  generateCategoryAiReport,
  previewCategoryAiReport,
} from '../../services/CategoryService';

interface CategoryReportDialogProps {
  open: boolean;
  onClose: () => void;
  category: Category | null;
}

function treeDiagramSectionTitle(categoryName: string, reportIsRtl: boolean): string {
  if (contentTextLooksRtl(categoryName) || reportIsRtl) {
    return 'מבנה העץ';
  }
  return 'Category Structure';
}

function dataUrlToUint8Array(dataUrl: string): Uint8Array {
  const base64 = dataUrl.split(',')[1] ?? '';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function loadImageDimensions(dataUrl: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error('Failed to load diagram image'));
    img.src = dataUrl;
  });
}

const ReportTreeDiagramCapture: React.FC<{
  tree: Category;
  orientation: GraphicalOrientation;
  onCaptured: (dataUrl: string) => void;
  onFailed: () => void;
}> = ({ tree, orientation, onCaptured, onFailed }) => {
  const captureRef = useRef<HTMLDivElement | null>(null);
  const captureStartedRef = useRef(false);
  const isRTL = useMemo(() => /[\u0590-\u05FF]/.test(tree.name), [tree.name]);

  const runCapture = useCallback(async () => {
    const node = captureRef.current;
    if (!node || captureStartedRef.current) return;
    captureStartedRef.current = true;
    try {
      await new Promise<void>((resolve) => setTimeout(resolve, 50));
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });
      if (typeof document !== 'undefined' && document.fonts?.ready) {
        await document.fonts.ready;
      }
      const dataUrl = await toPng(node, {
        cacheBust: true,
        backgroundColor: '#ffffff',
        pixelRatio:
          orientation === 'horizontal'
            ? Math.max(3, Math.round((window.devicePixelRatio || 1) * 3))
            : Math.max(2, Math.round((window.devicePixelRatio || 1) * 2)),
      });
      if (!dataUrl || dataUrl.length < 2000) {
        onFailed();
        return;
      }
      onCaptured(dataUrl);
    } catch {
      onFailed();
    }
  }, [onCaptured, onFailed, orientation]);

  const handleLayout = useCallback(() => {
    void runCapture();
  }, [runCapture]);

  useEffect(() => {
    captureStartedRef.current = false;
    const fallbackTimer = window.setTimeout(() => {
      void runCapture();
    }, 2500);
    return () => window.clearTimeout(fallbackTimer);
  }, [tree, orientation, runCapture]);

  return createPortal(
    <Box
      aria-hidden
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        pointerEvents: 'none',
        overflow: 'visible',
        zIndex: -1,
      }}
    >
      <Box
        ref={captureRef}
        sx={{
          bgcolor: '#ffffff',
          width: 'max-content',
          minWidth: 'max-content',
          display: 'inline-block',
        }}
      >
        <SubTreeGraph
          rootCategory={tree}
          isRTL={isRTL}
          orientation={orientation}
          selectedSubcases={[]}
          coverageShading={false}
          showSubcaseList={false}
          showContentCount
          forPngExport
          onLayout={handleLayout}
        />
      </Box>
    </Box>,
    document.body,
  );
};

function ReportTreeDiagramSection({
  dataUrl,
  title,
  isRtl,
}: {
  dataUrl: string;
  title: string;
  isRtl: boolean;
}) {
  return (
    <Box sx={{ mt: 4, pt: 2, borderTop: (t) => `1px solid ${t.palette.divider}` }}>
      <Typography
        component="h3"
        variant="h6"
        sx={{ fontWeight: 700, mb: 1.5 }}
        dir={isRtl ? 'rtl' : 'ltr'}
      >
        {title}
      </Typography>
      <Box
        component="img"
        src={dataUrl}
        alt={title}
        sx={{
          display: 'block',
          maxWidth: '100%',
          height: 'auto',
          borderRadius: 1,
          border: (t) => `1px solid ${t.palette.divider}`,
        }}
      />
    </Box>
  );
}

function buildTreeDiagramPrintHtml(
  dataUrl: string,
  title: string,
  isRtl: boolean,
  orientation: GraphicalOrientation,
): string {
  const align = isRtl ? 'right' : 'left';
  // Horizontal diagrams are often very wide; keep full page width but mark orientation for CSS.
  return `<h2 style="text-align:${align}">${escapeHtml(title)}</h2>
<img class="structure-diagram structure-diagram-${orientation}" src="${dataUrl}" alt="${escapeHtml(title)}" />`;
}

function extractApiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const data = (err as { response?: { data?: unknown } }).response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object' && 'message' in data) {
      const msg = (data as { message?: unknown }).message;
      if (typeof msg === 'string' && msg.trim()) return msg;
    }
  }
  return fallback;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

type ReportTextBlock = {
  type: 'h1' | 'h2' | 'h3' | 'quote' | 'p';
  content: string;
};
type ReportListBlock = {
  type: 'ul' | 'ol';
  items: string[];
};
type ReportBlock = ReportTextBlock | ReportListBlock;

function isReportListBlock(block: ReportBlock): block is ReportListBlock {
  return block.type === 'ul' || block.type === 'ol';
}

const UNORDERED_LIST_RE = /^[-*]\s+(.+)$/;
const ORDERED_LIST_RE = /^\d+\.\s+(.+)$/;

function parseReportMarkdownBlocks(text: string): ReportBlock[] {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReportBlock[] = [];
  let paragraphBuf: string[] = [];
  let listBuf: { type: 'ul' | 'ol'; items: string[] } | null = null;

  const flushParagraph = () => {
    if (paragraphBuf.length > 0) {
      blocks.push({ type: 'p', content: paragraphBuf.join('\n') });
      paragraphBuf = [];
    }
  };

  const flushList = () => {
    if (listBuf && listBuf.items.length > 0) {
      blocks.push({ type: listBuf.type, items: [...listBuf.items] });
      listBuf = null;
    }
  };

  for (const rawLine of lines) {
    const trimmed = rawLine.trim();
    if (!trimmed) {
      flushList();
      flushParagraph();
      continue;
    }
    if (trimmed.startsWith('### ')) {
      flushList();
      flushParagraph();
      blocks.push({ type: 'h3', content: trimmed.slice(4).trim() });
      continue;
    }
    if (trimmed.startsWith('## ')) {
      flushList();
      flushParagraph();
      blocks.push({ type: 'h2', content: trimmed.slice(3).trim() });
      continue;
    }
    if (trimmed.startsWith('# ')) {
      flushList();
      flushParagraph();
      blocks.push({ type: 'h1', content: trimmed.slice(2).trim() });
      continue;
    }
    const quoteText = trimmed.startsWith('> ')
      ? trimmed.slice(2).trim()
      : trimmed.startsWith('>')
        ? trimmed.slice(1).trim()
        : null;
    if (quoteText) {
      flushList();
      flushParagraph();
      blocks.push({ type: 'quote', content: quoteText });
      continue;
    }
    const ulMatch = trimmed.match(UNORDERED_LIST_RE);
    if (ulMatch) {
      flushParagraph();
      if (listBuf?.type === 'ul') {
        listBuf.items.push(ulMatch[1].trim());
      } else {
        flushList();
        listBuf = { type: 'ul', items: [ulMatch[1].trim()] };
      }
      continue;
    }
    const olMatch = trimmed.match(ORDERED_LIST_RE);
    if (olMatch) {
      flushParagraph();
      if (listBuf?.type === 'ol') {
        listBuf.items.push(olMatch[1].trim());
      } else {
        flushList();
        listBuf = { type: 'ol', items: [olMatch[1].trim()] };
      }
      continue;
    }
    flushList();
    paragraphBuf.push(rawLine.trimEnd());
  }
  flushList();
  flushParagraph();
  return blocks;
}

function renderInlineMarkdown(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}

function ReportMarkdownBody({ text, isRtl }: { text: string; isRtl: boolean }) {
  const theme = useTheme();
  const blocks = useMemo(() => parseReportMarkdownBlocks(text), [text]);
  const quoteBorder = `4px solid ${theme.palette.primary.main}`;

  return (
    <Box
      dir={isRtl ? 'rtl' : 'ltr'}
      sx={{
        direction: isRtl ? 'rtl' : 'ltr',
        textAlign: isRtl ? 'right' : 'left',
        fontSize: '1.05rem',
        lineHeight: 1.7,
      }}
    >
      {blocks.map((block, index) => {
        if (isReportListBlock(block)) {
          const ListTag = block.type === 'ul' ? 'ul' : 'ol';
          return (
            <Box
              key={index}
              component={ListTag}
              sx={{
                my: 1.25,
                pl: isRtl ? 0 : 2.5,
                pr: isRtl ? 2.5 : 0,
                '& li': { mb: 0.5 },
              }}
            >
              {block.items.map((item, itemIndex) => (
                <Typography
                  key={itemIndex}
                  component="li"
                  variant="body1"
                  sx={{ display: 'list-item' }}
                >
                  {renderInlineMarkdown(item)}
                </Typography>
              ))}
            </Box>
          );
        }

        const inline = renderInlineMarkdown(block.content);
        switch (block.type) {
          case 'h1':
            return (
              <Typography
                key={index}
                component="h2"
                variant="h5"
                sx={{ fontWeight: 700, mt: index === 0 ? 0 : 3, mb: 1.25 }}
              >
                {inline}
              </Typography>
            );
          case 'h2':
            return (
              <Typography
                key={index}
                component="h3"
                variant="h6"
                sx={{ fontWeight: 700, mt: 3, mb: 1 }}
              >
                {inline}
              </Typography>
            );
          case 'h3':
            return (
              <Typography
                key={index}
                component="h4"
                variant="subtitle1"
                sx={{ fontWeight: 600, mt: 2.5, mb: 0.75 }}
              >
                {inline}
              </Typography>
            );
          case 'quote':
            return (
              <Box
                key={index}
                component="blockquote"
                sx={{
                  my: 1.5,
                  mx: 0,
                  pl: isRtl ? 0 : 2,
                  pr: isRtl ? 2 : 0,
                  borderLeft: isRtl ? 'none' : quoteBorder,
                  borderRight: isRtl ? quoteBorder : 'none',
                  color: 'text.secondary',
                  fontStyle: 'italic',
                }}
              >
                <Typography component="p" variant="body1" sx={{ m: 0 }}>
                  {inline}
                </Typography>
              </Box>
            );
          default:
            return (
              <Typography
                key={index}
                component="p"
                variant="body1"
                sx={{ m: 0, mb: 1.25, whiteSpace: 'pre-wrap' }}
              >
                {inline}
              </Typography>
            );
        }
      })}
    </Box>
  );
}

function formatReportMarkdownInlineHtml(text: string): string {
  return escapeHtml(text).replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
}

function reportMarkdownToHtmlBody(body: string, isRtl: boolean): string {
  const align = isRtl ? 'right' : 'left';
  return parseReportMarkdownBlocks(body)
    .map((block) => {
      if (isReportListBlock(block)) {
        const tag = block.type;
        const items = block.items
          .map((item) => `<li>${formatReportMarkdownInlineHtml(item)}</li>`)
          .join('');
        return `<${tag} style="text-align:${align};margin:0 0 1rem;padding-${isRtl ? 'right' : 'left'}:1.5rem;">${items}</${tag}>`;
      }
      const content = formatReportMarkdownInlineHtml(block.content);
      switch (block.type) {
        case 'h1':
          return `<h1 style="text-align:${align}">${content}</h1>`;
        case 'h2':
          return `<h2 style="text-align:${align}">${content}</h2>`;
        case 'h3':
          return `<h3 style="text-align:${align}">${content}</h3>`;
        case 'quote':
          return `<blockquote style="text-align:${align};font-style:italic;margin:1rem 0;padding-${isRtl ? 'right' : 'left'}:1rem;border-${isRtl ? 'right' : 'left'}:4px solid #1976d2;">${content}</blockquote>`;
        default:
          return `<p style="text-align:${align};white-space:pre-wrap;margin:0 0 1rem;">${content.replace(/\n/g, '<br>')}</p>`;
      }
    })
    .join('\n');
}

function buildReportPrintHtml(options: {
  title: string;
  subtitle: string;
  body: string;
  isRtl: boolean;
  treeDiagramDataUrl?: string;
  treeDiagramTitle?: string;
  treeDiagramOrientation?: GraphicalOrientation;
}): string {
  const dir = options.isRtl ? 'rtl' : 'ltr';
  const align = options.isRtl ? 'right' : 'left';
  const diagramHtml =
    options.treeDiagramDataUrl && options.treeDiagramTitle
      ? `<hr/>${buildTreeDiagramPrintHtml(
          options.treeDiagramDataUrl,
          options.treeDiagramTitle,
          options.isRtl,
          options.treeDiagramOrientation ?? 'vertical',
        )}`
      : '';
  return `<!DOCTYPE html>
<html dir="${dir}">
<head>
  <meta charset="utf-8">
  <title>${escapeHtml(options.title)}</title>
  <style>
    body {
      font-family: system-ui, -apple-system, sans-serif;
      margin: 2rem;
      line-height: 1.65;
      direction: ${dir};
      text-align: ${align};
    }
    h1 { font-size: 1.5rem; margin: 1.5rem 0 0.5rem; font-weight: 700; }
    h2 { font-size: 1.25rem; margin: 1.25rem 0 0.5rem; font-weight: 700; }
    h3 { font-size: 1.1rem; margin: 1rem 0 0.35rem; font-weight: 600; }
    .meta { color: #555; margin: 0 0 1.25rem; font-style: italic; }
    p { margin: 0 0 1rem; white-space: pre-wrap; }
    blockquote { font-style: italic; color: #444; }
    ul, ol { margin: 0 0 1rem; padding-${options.isRtl ? 'right' : 'left'}: 1.5rem; }
    img { max-width: 100%; height: auto; }
    img.structure-diagram {
      display: block;
      width: 100%;
      max-width: 100%;
      height: auto;
      margin: 0 0 1.5rem;
      page-break-inside: avoid;
    }
    @media print {
      body { margin: 1rem; }
      img.structure-diagram {
        width: 100%;
        max-width: 100%;
      }
      /* Prefer landscape for wide top-down diagrams when the printer supports it. */
      img.structure-diagram-horizontal {
        width: 100%;
      }
      @page {
        size: auto;
        margin: 12mm;
      }
    }
  </style>
</head>
<body>
  <h1>${escapeHtml(options.title)}</h1>
  <p class="meta">${escapeHtml(options.subtitle)}</p>
  <hr/>
  ${reportMarkdownToHtmlBody(options.body, options.isRtl)}
  ${diagramHtml}
</body>
</html>`;
}

function printReportDocument(html: string): void {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const printWindow = window.open(url, '_blank');
  if (!printWindow) {
    URL.revokeObjectURL(url);
    return;
  }
  const triggerPrint = () => {
    try {
      printWindow.focus();
      printWindow.print();
    } finally {
      URL.revokeObjectURL(url);
    }
  };
  printWindow.addEventListener('load', triggerPrint);
  setTimeout(() => {
    try {
      if (!printWindow.closed && printWindow.document?.readyState === 'complete') {
        triggerPrint();
      }
    } catch {
      // Blob navigation may restrict access; load handler usually suffices.
    }
  }, 600);
}

function makeSafeFilename(name: string): string {
  return (name || 'report')
    .replace(/[<>:"/\\|?*]/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function paragraphAlignment(isRtl: boolean): (typeof AlignmentType)[keyof typeof AlignmentType] {
  return isRtl ? AlignmentType.RIGHT : AlignmentType.LEFT;
}

/** Split report markdown-ish text into docx paragraphs (headings, quotes, body). */
function buildReportDocxParagraphs(report: string, isRtl: boolean): Paragraph[] {
  const align = paragraphAlignment(isRtl);
  const paragraphs: Paragraph[] = [];

  for (const block of parseReportMarkdownBlocks(report)) {
    if (isReportListBlock(block)) {
      block.items.forEach((item, index) => {
        const plain = item.replace(/\*\*/g, '');
        const prefix = block.type === 'ol' ? `${index + 1}. ` : '• ';
        paragraphs.push(
          new Paragraph({
            children: [new TextRun({ text: `${prefix}${plain}` })],
            alignment: align,
            indent: isRtl ? { right: 360 } : { left: 360 },
          }),
        );
      });
      continue;
    }
    const plain = block.content.replace(/\*\*/g, '');
    switch (block.type) {
      case 'h1':
        paragraphs.push(
          new Paragraph({ text: plain, heading: HeadingLevel.HEADING_1, alignment: align }),
        );
        break;
      case 'h2':
        paragraphs.push(
          new Paragraph({ text: plain, heading: HeadingLevel.HEADING_2, alignment: align }),
        );
        break;
      case 'h3':
        paragraphs.push(
          new Paragraph({ text: plain, heading: HeadingLevel.HEADING_3, alignment: align }),
        );
        break;
      case 'quote':
        paragraphs.push(
          new Paragraph({
            children: [new TextRun({ text: plain, italics: true })],
            alignment: align,
            indent: isRtl ? { right: 720 } : { left: 720 },
          }),
        );
        break;
      default:
        paragraphs.push(
          new Paragraph({
            children: [new TextRun({ text: plain })],
            alignment: align,
          }),
        );
        break;
    }
  }

  return paragraphs;
}

async function buildTreeDiagramDocxParagraphs(
  dataUrl: string,
  title: string,
  isRtl: boolean,
): Promise<Paragraph[]> {
  const { width, height } = await loadImageDimensions(dataUrl);
  const maxWidth = 600;
  const scale = width > maxWidth ? maxWidth / width : 1;
  const imageWidth = Math.max(1, Math.round(width * scale));
  const imageHeight = Math.max(1, Math.round(height * scale));
  const bytes = dataUrlToUint8Array(dataUrl);
  return [
    new Paragraph({
      text: title,
      heading: HeadingLevel.HEADING_2,
      alignment: paragraphAlignment(isRtl),
    }),
    new Paragraph({
      children: [
        new ImageRun({
          type: 'png',
          data: bytes,
          transformation: { width: imageWidth, height: imageHeight },
        }),
      ],
      alignment: AlignmentType.CENTER,
    }),
    new Paragraph({ text: '', alignment: paragraphAlignment(isRtl) }),
  ];
}

async function exportReportDocx(options: {
  title: string;
  ancestorPath: string;
  body: string;
  isRtl: boolean;
  selectionNote?: string;
  treeDiagramDataUrl?: string;
  treeDiagramTitle?: string;
}): Promise<void> {
  const align = paragraphAlignment(options.isRtl);
  const children: Paragraph[] = [
    new Paragraph({
      text: `Report: ${options.title}`,
      heading: HeadingLevel.HEADING_1,
      alignment: align,
    }),
    new Paragraph({
      text: options.ancestorPath,
      alignment: align,
    }),
    new Paragraph({
      text: `Generated on: ${new Date().toLocaleString()}`,
      alignment: align,
    }),
  ];

  if (options.selectionNote?.trim()) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: options.selectionNote, italics: true })],
        alignment: align,
      }),
    );
  }

  children.push(new Paragraph({ text: '', alignment: align }));
  children.push(...buildReportDocxParagraphs(options.body, options.isRtl));

  if (options.treeDiagramDataUrl && options.treeDiagramTitle) {
    children.push(
      ...(await buildTreeDiagramDocxParagraphs(
        options.treeDiagramDataUrl,
        options.treeDiagramTitle,
        options.isRtl,
      )),
    );
  }

  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 },
          },
        },
        children,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `report-${makeSafeFilename(options.title)}.docx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

type DialogPhase = 'setup' | 'reading';

/** Bump when report diagram capture styling changes so stale PNGs are not reused. */
const STRUCTURE_DIAGRAM_CAPTURE_REV = 'v9';

function structureDiagramCacheKey(orientation: GraphicalOrientation): string {
  return `${orientation}:${STRUCTURE_DIAGRAM_CAPTURE_REV}`;
}

const CategoryReportDialog: React.FC<CategoryReportDialogProps> = ({
  open,
  onClose,
  category,
}) => {
  const [phase, setPhase] = useState<DialogPhase>('setup');
  const [readerFullScreen, setReaderFullScreen] = useState(false);
  const [preview, setPreview] = useState<CategoryReportPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [focus, setFocus] = useState('');
  const [questions, setQuestions] = useState('');
  const [showTreeDiagram, setShowTreeDiagram] = useState(false);
  const [treeDiagramOrientation, setTreeDiagramOrientation] =
    useState<GraphicalOrientation>('vertical');
  const [report, setReport] = useState('');
  const [treeDiagramCache, setTreeDiagramCache] = useState<Record<string, string>>({});
  const [reportTreeCategory, setReportTreeCategory] = useState<Category | null>(null);
  const [diagramCaptureTree, setDiagramCaptureTree] = useState<{
    tree: Category;
    orientation: GraphicalOrientation;
  } | null>(null);
  const [capturingTreeDiagram, setCapturingTreeDiagram] = useState(false);
  const [selectionNote, setSelectionNote] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const [copyNotice, setCopyNotice] = useState('');
  const [exportingDocx, setExportingDocx] = useState(false);
  const previewTokenRef = useRef(0);
  const diagramCaptureResolverRef = useRef<((dataUrl: string | null) => void) | null>(null);

  const reportIsRtl = useMemo(
    () =>
      contentTextLooksRtl(
        [report, preview?.ancestorPath ?? '', preview?.categoryName ?? ''].join('\n'),
      ),
    [report, preview?.ancestorPath, preview?.categoryName],
  );

  const treeDiagramTitle = useMemo(
    () =>
      preview
        ? treeDiagramSectionTitle(preview.categoryName, reportIsRtl)
        : 'Category Structure',
    [preview, reportIsRtl],
  );

  const activeTreeDiagramDataUrl =
    showTreeDiagram && treeDiagramCache[structureDiagramCacheKey(treeDiagramOrientation)]
      ? treeDiagramCache[structureDiagramCacheKey(treeDiagramOrientation)]
      : null;

  const resetForm = useCallback(() => {
    setPhase('setup');
    setReaderFullScreen(false);
    setPreview(null);
    setPreviewError('');
    setFocus('');
    setQuestions('');
    setShowTreeDiagram(false);
    setTreeDiagramOrientation('vertical');
    setReport('');
    setTreeDiagramCache({});
    setReportTreeCategory(null);
    setDiagramCaptureTree(null);
    setCapturingTreeDiagram(false);
    diagramCaptureResolverRef.current = null;
    setSelectionNote('');
    setGenerateError('');
    setCopyNotice('');
  }, []);

  useEffect(() => {
    if (!open || !category) {
      resetForm();
      return;
    }
    const token = ++previewTokenRef.current;
    setPreviewLoading(true);
    setPreviewError('');
    setPreview(null);
    setPhase('setup');
    setReport('');
    void previewCategoryAiReport(category.id)
      .then((data) => {
        if (previewTokenRef.current !== token) return;
        setPreview(data);
      })
      .catch((err) => {
        if (previewTokenRef.current !== token) return;
        setPreviewError(extractApiErrorMessage(err, 'Could not load report scope.'));
      })
      .finally(() => {
        if (previewTokenRef.current === token) setPreviewLoading(false);
      });
  }, [open, category, resetForm]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const captureTreeDiagram = useCallback(
    (tree: Category, orientation: GraphicalOrientation): Promise<string | null> => {
      return new Promise((resolve) => {
        diagramCaptureResolverRef.current = resolve;
        setDiagramCaptureTree({ tree, orientation });
      });
    },
    [],
  );

  const handleDiagramCaptured = useCallback((dataUrl: string) => {
    diagramCaptureResolverRef.current?.(dataUrl);
    diagramCaptureResolverRef.current = null;
    setDiagramCaptureTree(null);
  }, []);

  const handleDiagramCaptureFailed = useCallback(() => {
    diagramCaptureResolverRef.current?.(null);
    diagramCaptureResolverRef.current = null;
    setDiagramCaptureTree(null);
  }, []);

  const ensureTreeDiagramCaptured = useCallback(
    async (orientation: GraphicalOrientation): Promise<string | null> => {
      const key = structureDiagramCacheKey(orientation);
      const cached = treeDiagramCache[key];
      if (cached) return cached;
      if (!category) return null;
      let tree = reportTreeCategory;
      if (!tree) {
        tree = await fetchCategoryWithChildren(category.id);
        setReportTreeCategory(tree);
      }
      const dataUrl = await captureTreeDiagram(tree, orientation);
      if (dataUrl) {
        setTreeDiagramCache((prev) => ({ ...prev, [key]: dataUrl }));
      }
      return dataUrl;
    },
    [treeDiagramCache, category, reportTreeCategory, captureTreeDiagram],
  );

  const handleTreeDiagramToggle = async (enabled: boolean) => {
    setCopyNotice('');
    if (!enabled) {
      setShowTreeDiagram(false);
      return;
    }
    if (treeDiagramCache[structureDiagramCacheKey(treeDiagramOrientation)]) {
      setShowTreeDiagram(true);
      return;
    }
    setCapturingTreeDiagram(true);
    try {
      const dataUrl = await ensureTreeDiagramCaptured(treeDiagramOrientation);
      if (dataUrl) {
        setShowTreeDiagram(true);
      } else {
        setCopyNotice('Category structure diagram could not be captured.');
        setShowTreeDiagram(false);
      }
    } catch {
      setCopyNotice('Could not load category structure for diagram.');
      setShowTreeDiagram(false);
    } finally {
      setCapturingTreeDiagram(false);
    }
  };

  const handleTreeDiagramOrientationChange = async (
    orientation: GraphicalOrientation | null,
  ) => {
    if (!orientation || orientation === treeDiagramOrientation) return;
    setCopyNotice('');
    setTreeDiagramOrientation(orientation);
    if (!showTreeDiagram) return;
    if (treeDiagramCache[structureDiagramCacheKey(orientation)]) return;
    setCapturingTreeDiagram(true);
    try {
      const dataUrl = await ensureTreeDiagramCaptured(orientation);
      if (!dataUrl) {
        setCopyNotice('Category structure diagram could not be captured.');
      }
    } catch {
      setCopyNotice('Could not load category structure for diagram.');
    } finally {
      setCapturingTreeDiagram(false);
    }
  };

  const handleGenerate = async () => {
    if (!category || !preview) return;
    setGenerating(true);
    setGenerateError('');
    setReport('');
    setTreeDiagramCache({});
    setReportTreeCategory(null);
    setShowTreeDiagram(false);
    setTreeDiagramOrientation('vertical');
    setSelectionNote('');
    setCopyNotice('');
    try {
      const result = await generateCategoryAiReport({
        categoryId: category.id,
        focus: focus.trim() || undefined,
        questions: questions.trim() || undefined,
      });
      setReport(result.report);
      const notes: string[] = [];
      if (result.semanticallySelected && result.contentRowCount > result.rowsIncluded) {
        notes.push(
          `Report based on the ${result.rowsIncluded} most relevant rows (of ${result.contentRowCount} in scope). Add focus or questions to steer selection.`,
        );
      }
      setSelectionNote(notes.join(' '));
      setPhase('reading');
    } catch (err) {
      setGenerateError(extractApiErrorMessage(err, 'Failed to generate report.'));
    } finally {
      setGenerating(false);
    }
  };

  const usesSemanticSelection =
    preview?.usesSemanticSelection ?? preview?.overRowLimit ?? false;

  const handleCopyReport = async () => {
    if (!report) return;
    try {
      let text = report;
      if (activeTreeDiagramDataUrl) {
        text += `\n\n[${treeDiagramTitle} diagram included in print and Word export]`;
      }
      await navigator.clipboard.writeText(text);
      setCopyNotice('Report copied to clipboard.');
    } catch {
      setCopyNotice('Could not copy to clipboard.');
    }
  };

  const handlePrint = () => {
    if (!report || !preview) return;
    printReportDocument(
      buildReportPrintHtml({
        title: preview.categoryName,
        subtitle: preview.ancestorPath,
        body: report,
        isRtl: reportIsRtl,
        treeDiagramDataUrl: activeTreeDiagramDataUrl ?? undefined,
        treeDiagramTitle: activeTreeDiagramDataUrl ? treeDiagramTitle : undefined,
        treeDiagramOrientation: treeDiagramOrientation,
      }),
    );
  };

  const handleExportDocx = async () => {
    if (!report || !preview || exportingDocx) return;
    setExportingDocx(true);
    setCopyNotice('');
    try {
      await exportReportDocx({
        title: preview.categoryName,
        ancestorPath: preview.ancestorPath,
        body: report,
        isRtl: reportIsRtl,
        selectionNote: selectionNote || undefined,
        treeDiagramDataUrl: activeTreeDiagramDataUrl ?? undefined,
        treeDiagramTitle: activeTreeDiagramDataUrl ? treeDiagramTitle : undefined,
      });
      setCopyNotice('Report exported to Word (.docx).');
    } catch {
      setCopyNotice('Could not export to Word.');
    } finally {
      setExportingDocx(false);
    }
  };

  const scopeLabel =
    preview?.scopeType === 'BRANCH' ? 'Branch subtree' : 'Content category';

  if (phase === 'reading' && report && preview) {
    return (
      <Dialog
        open={open}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return;
          handleClose();
        }}
        fullScreen={readerFullScreen}
        maxWidth="xl"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: readerFullScreen
            ? { display: 'flex', flexDirection: 'column', height: '100%', m: 0, maxHeight: '100%' }
            : {
                display: 'flex',
                flexDirection: 'column',
                maxHeight: 'min(94vh, 960px)',
                width: '100%',
                m: { xs: 0.5, sm: 1 },
              },
        }}
      >
        <AppBar position="sticky" color="default" elevation={1}>
          <Toolbar variant="dense" sx={{ gap: 0.5, minHeight: 48, px: { xs: 1, sm: 1.5 } }}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setPhase('setup')}
              aria-label="Back to report options"
            >
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" component="div" sx={{ fontWeight: 600 }} noWrap>
                Report: {preview.categoryName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {preview.ancestorPath}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showTreeDiagram}
                  onChange={(e) => void handleTreeDiagramToggle(e.target.checked)}
                  disabled={capturingTreeDiagram}
                />
              }
              label={capturingTreeDiagram ? 'Structure diagram…' : 'Structure diagram'}
              sx={{ mx: 0.5, flexShrink: 0 }}
            />
            <ToggleButtonGroup
              size="small"
              exclusive
              value={treeDiagramOrientation}
              onChange={(_e, value: GraphicalOrientation | null) =>
                void handleTreeDiagramOrientationChange(value)
              }
              disabled={capturingTreeDiagram}
              sx={{ flexShrink: 0, mr: 0.5 }}
            >
              <ToggleButton value="vertical" aria-label="Vertical category structure diagram">
                Vertical
              </ToggleButton>
              <ToggleButton value="horizontal" aria-label="Horizontal category structure diagram">
                Horizontal
              </ToggleButton>
            </ToggleButtonGroup>
            <Button size="small" onClick={() => void handleCopyReport()}>
              Copy
            </Button>
            <Button size="small" onClick={handlePrint}>
              Print
            </Button>
            <Button size="small" onClick={() => void handleExportDocx()} disabled={exportingDocx}>
              {exportingDocx ? 'Exporting…' : 'Export DOCX'}
            </Button>
            <IconButton
              color="inherit"
              onClick={() => setReaderFullScreen((v) => !v)}
              aria-label={readerFullScreen ? 'Exit full screen' : 'Full screen'}
            >
              {readerFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
            <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="Close report">
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            minHeight: 0,
            p: { xs: 1.5, sm: 2.5 },
            bgcolor: 'background.default',
          }}
        >
          {selectionNote ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {selectionNote}
            </Alert>
          ) : null}
          {copyNotice ? (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              {copyNotice}
            </Typography>
          ) : null}
          <Box
            sx={(t) => ({
              maxWidth: readerFullScreen ? '56rem' : '48rem',
              mx: 'auto',
              p: { xs: 2, sm: 3 },
              borderRadius: 1,
              bgcolor: 'background.paper',
              border: `1px solid ${t.palette.divider}`,
              boxShadow: t.shadows[1],
              minHeight: readerFullScreen ? 'calc(100dvh - 140px)' : 'min(70vh, 640px)',
              overflowY: 'auto',
            })}
          >
            <ReportMarkdownBody text={report} isRtl={reportIsRtl} />
            {activeTreeDiagramDataUrl ? (
              <ReportTreeDiagramSection
                dataUrl={activeTreeDiagramDataUrl}
                title={treeDiagramTitle}
                isRtl={reportIsRtl}
              />
            ) : null}
          </Box>
        </Box>
        {diagramCaptureTree ? (
          <ReportTreeDiagramCapture
            tree={diagramCaptureTree.tree}
            orientation={diagramCaptureTree.orientation}
            onCaptured={handleDiagramCaptured}
            onFailed={handleDiagramCaptureFailed}
          />
        ) : null}
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        handleClose();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Generate report (AI)</DialogTitle>
      <DialogContent>
        {previewLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
            <CircularProgress size={22} />
            <Typography variant="body2">Loading scope…</Typography>
          </Box>
        ) : null}
        {previewError ? <Alert severity="error" sx={{ mb: 2 }}>{previewError}</Alert> : null}
        {preview ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {scopeLabel}: <strong>{preview.categoryName}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Path: {preview.ancestorPath}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {preview.contentRowCount} content row{preview.contentRowCount === 1 ? '' : 's'} in scope
              {usesSemanticSelection
                ? ` — report will use the ${preview.maxRows} most relevant`
                : ''}
            </Typography>
          </Box>
        ) : null}
        {usesSemanticSelection ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            This scope has more than {preview?.maxRows} rows. The report will include the most
            relevant passages (category path, focus, and questions). Add focus or questions to
            steer what is selected.
          </Alert>
        ) : null}
        {preview && !previewError ? (
          <>
            <TextField
              fullWidth
              label="Focus (optional)"
              placeholder="What should the report emphasize? e.g. contradictions between subcases, how the story unfolds, key tensions…"
              value={focus}
              onChange={(e) => setFocus(e.target.value)}
              multiline
              minRows={2}
              margin="normal"
              disabled={generating}
            />
            <TextField
              fullWidth
              label="Questions to answer (optional)"
              placeholder="One question per line"
              value={questions}
              onChange={(e) => setQuestions(e.target.value)}
              minRows={3}
              multiline
              margin="normal"
              disabled={generating}
            />
          </>
        ) : null}
        {generateError ? <Alert severity="error" sx={{ mt: 1 }}>{generateError}</Alert> : null}
        {generating ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
            <CircularProgress size={22} />
            <Typography variant="body2">Generating report…</Typography>
          </Box>
        ) : null}
        {report && phase === 'setup' ? (
          <Alert severity="success" sx={{ mt: 2 }}>
            Report ready.{' '}
            <Button size="small" onClick={() => setPhase('reading')}>
              Open report
            </Button>
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        <Button
          variant="contained"
          onClick={() => void handleGenerate()}
          disabled={!preview || !!previewError || previewLoading || generating}
        >
          {report ? 'Regenerate report' : 'Generate report'}
        </Button>
      </DialogActions>
      {diagramCaptureTree ? (
        <ReportTreeDiagramCapture
          tree={diagramCaptureTree.tree}
          orientation={diagramCaptureTree.orientation}
          onCaptured={handleDiagramCaptured}
          onFailed={handleDiagramCaptureFailed}
        />
      ) : null}
    </Dialog>
  );
};

export default CategoryReportDialog;
